import type {
  CurrentFile,
  Response,
  StoreThis,
  FileModelType,
  UploadResponse,
  ID,
  Maybe,
} from '@/types'
import type { ToastState } from '@/types/ui'

import { defineStore } from 'pinia'
import { useUiStore } from '@/stores/ui'
import { useModelsStore } from '@/stores/models'

import { handleErrors } from '@/helpers'
import { ref } from 'vue'

const resource = 'files'

export const useFilesStore = defineStore(resource, () => {
  const modelsStore = useModelsStore()
  const ui = useUiStore()

  const currentFiles = ref<CurrentFile[]>([])
  const currentMoreFiles = ref<CurrentFile[]>([])
  const removedFiles = ref<(CurrentFile | FileModelType)[]>([])
  const removedMoreFiles = ref<CurrentFile[]>([])

  const isLoading = ref(false)

  async function upload(
    this: StoreThis,
    fileList: CurrentFile[] = currentFiles.value,
    id?: number,
    model: 'block' | 'preset' | 'product' | 'brand' | 'posting' = 'block',
    flag?: 'product_more' | 'product_main' | 'package',
    sort?: number[]
  ): Promise<UploadResponse> {
    const config = { formData: true }
    const options: Partial<ToastState> = {}
    let data: FileModelType[] = []
    let error = false

    const filesIndexes: number[] = []
    const files = fileList.reduce((items: File[], item, currentIndex) => {
      if (!item.file) return items

      items.push(item.file)
      filesIndexes.push(currentIndex)

      return items
    }, [])

    if (!files.length) return { error, data }

    let url = `/m/${resource}/${model}`
    if (Number.isInteger(id)) url = `${url}/${id}`

    isLoading.value = true

    const payload = new FormData()

    files.forEach((file) => payload.append('files[]', file))

    if (flag) payload.append('flag', flag)
    /**
     * Добавляем сортировку (к какому виду относится) для изображений товаров, если не указана вручную
     * */
    if (sort) sort.forEach((s) => payload.append('sort[]', s.toString()))
    else if (flag === 'product_main') {
      const list = fileList.reduce((items: number[], item) => {
        if (!item.file) return items

        items.push(item.sort)

        return items
      }, [])

      list.forEach((s) => payload.append('sort[]', s.toString()))
    }

    try {
      const {
        data: { success, errors, data: responseData },
      }: Response<FileModelType[]> = await this.rest.post(url, payload, config)

      if (!success && errors) throw Error(handleErrors(errors))

      data = responseData
      /**
       * Из расчета, что с бэка приходит в той же последовательности, что и сохранялось
       * */
      responseData.forEach((item, index) => {
        const fileIndex = filesIndexes[index]

        fileList[fileIndex].id = item.id ?? null
        fileList[fileIndex].path = item.path ?? null
      })
    } catch (e) {
      error = true
      console.error('[upload]', e)

      options.text = 'Что-то пошло не так. Загрузить файлы не удалось'
    } finally {
      isLoading.value = false

      if (options.text) ui.showToast(options)
    }

    return { error, data }
  }

  async function remove(this: StoreThis, id?: Maybe<ID>) {
    if (!Number.isInteger(id)) return false

    const options: Partial<ToastState> = {}
    let error = false
    isLoading.value = true

    try {
      const {
        data: { success, errors },
      }: Response<any> = await this.rest.delete(`/m/${resource}/${id}`)

      if (!success && errors) throw Error(handleErrors(errors))
    } catch (e) {
      error = true
      console.error('[remove file]', id, e)

      options.text = 'Что-то пошло не так. Удалить файл не вышло'
    } finally {
      isLoading.value = false

      if (options.text) ui.showToast(options)
    }

    return error
  }

  function reset(current: CurrentFile[] = [], currentMore: CurrentFile[] = []) {
    currentFiles.value = current
    currentMoreFiles.value = currentMore
    removedFiles.value = []
    removedMoreFiles.value = []
  }

  return {
    currentFiles,
    currentMoreFiles,

    removedFiles,
    removedMoreFiles,

    isLoading,

    upload,
    remove,

    sort: (ids: ID[]) => modelsStore.sort(ids, 'file'),

    reset,
  }
})
